import type { DealListItem } from "@dishour/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unwrap } from "./client.js";

export const dealsKey = ["deals"] as const;
export const supplierDealsKey = ["supplier", "deals"] as const;

const DAY_MS = 86_400_000;

/**
 * Clearance deals live and die on their clock, so the card's visual urgency has to be
 * derived from the same clock the server enforces (an expired-but-unpublished deal is
 * still "urgent", not "normal").
 */
export function dealUrgency(expiresAt: string, now: Date = new Date()): "urgent" | "soon" | "normal" {
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (ms < DAY_MS) return "urgent";
  if (ms < 3 * DAY_MS) return "soon";
  return "normal";
}

const get = <T,>(path: string) => fetch(path, { credentials: "same-origin" }).then(unwrap<T>);
const post = <T,>(path: string, body?: unknown) =>
  fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).then(unwrap<T>);

export const useDeals = () => useQuery({ queryKey: dealsKey, queryFn: () => get<DealListItem[]>("/api/deals") });

export function useClaim(dealId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (qty: string) => post<DealListItem>(`/api/deals/${dealId}/claim`, { qty }),
    onSuccess: (updated) =>
      queryClient.setQueryData<DealListItem[]>(dealsKey, (prev) =>
        prev?.map((d) => (d.id === updated.id ? updated : d)),
      ),
  });
}

export const useSupplierDeals = () =>
  useQuery({ queryKey: supplierDealsKey, queryFn: () => get<DealListItem[]>("/api/supplier/deals") });

function useSupplierDealMutation<TVars>(fn: (vars: TVars) => Promise<DealListItem>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: supplierDealsKey }),
  });
}

export type CreateDealBody = {
  skuId: string | null;
  title: string;
  description: string | null;
  qtyAvailable: string;
  unitPrice: string;
  expiresAt: string;
};

export const useCreateDeal = () =>
  useSupplierDealMutation((body: CreateDealBody) => post<DealListItem>("/api/supplier/deals", body));
export const useCloseDeal = () =>
  useSupplierDealMutation((id: string) => post<DealListItem>(`/api/supplier/deals/${id}/close`));
